import { CommonModule } from '@angular/common';
import { Component, inject, Input, OnDestroy, OnInit, signal, WritableSignal, ChangeDetectionStrategy, Output, EventEmitter } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { SortDirection } from '@angular/material/sort';
import { Subject, takeUntil } from 'rxjs';
import { SchoolsService, ToastMessageService, LoadingSpinnerService } from '@k12/app-services';
import { AppInsightsService } from '@k12/error_handler';
import { School, SvgIcon, ToastMessageType, SeverityLevel } from '@k12/models';
import { CfiMatTableComponent, ColumnDefinition } from '@k12/tables';
import { Router } from '@angular/router';

// Extended types for API response with nested HDMA contact data
interface Phone {
  phoneNumber?: string;
  extension?: string;
}

interface PersonPhone {
  isPrimary?: boolean;
  phone?: Phone;
  extension?: string;
}

interface PersonEmail {
  isPrimary?: boolean;
  emailAddress?: string;
}

interface Person {
  firstName?: string;
  middleName?: string;
  lastName?: string;
  personEmails?: PersonEmail[];
  personPhones?: PersonPhone[];
}

interface HdmaContact {
  person?: Person;
}

interface SchoolWithHdma extends School {
  hdmaContact?: HdmaContact;
  schoolStatus?: string;
}

interface SchoolTableRow extends School {
  fullAddress?: string;
  hdmaName?: string;
  emails?: string;
  phoneNumber?: string;
  extension?: string;
  schoolStatus?: string;
}

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'lib-institution-schools',
  imports: [CfiMatTableComponent, CommonModule, MatButtonModule, MatIconModule, MatTooltipModule],
  standalone: true,
  templateUrl: './institution-schools.component.html',
  styleUrls: ['./institution-schools.component.scss']
})
export class InstitutionSchoolsComponent implements OnInit, OnDestroy {
  @Input() institutionId: string | null = null;
  @Input() sortKey: string = 'name';
  @Output() hyperlinkClicked = new EventEmitter<{ row: SchoolTableRow; column: string }>();

  // Column definitions for schools table
  columns: ColumnDefinition[] = [
    {
      name: 'name',
      header: 'School',
      sortable: true,
      width: 15,
      isHyperlink: true
    },
    {
      name: 'fullAddress',
      header: 'Address',
      sortable: false,
      width: 20
    },
    {
      name: 'hdmaName',
      header: 'HDMA Name',
      sortable: true,
      width: 15
    },
    {
      name: 'emails',
      header: 'Email',
      sortable: false,
      width: 18
    },
    {
      name: 'phoneNumber',
      header: 'Phone No.',
      sortable: false,
      width: 12
    },
    {
      name: 'extension',
      header: 'EXT.',
      sortable: false,
      width: 8
    },
    {
      name: 'schoolStatus',
      header: 'School Status',
      sortable: true,
      width: 12
    }
  ];

  schoolsTableData: WritableSignal<SchoolTableRow[]> = signal([]);
  displayedColumns: string[] = [];
  sortDir: SortDirection = 'asc';
  SvgIcon = SvgIcon;

  private readonly appInsightsService = inject(AppInsightsService);
  private readonly loadingSpinnerService = inject(LoadingSpinnerService);
  private readonly ngUnsubscribe: Subject<void> = new Subject();
  private readonly schoolsService = inject(SchoolsService);
  private readonly toastService = inject(ToastMessageService);
  private readonly router = inject(Router);

  ngOnInit(): void {
    this.displayedColumns = this.columns.map((col) => col.name);
    this.refreshSchools();
  }

  getTableClasses(): string[] {
    return ['full-width-table', 'schools-table'];
  }

  refreshSchools(): void {
    this.getInstitutionSchools();
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  private getInstitutionSchools(): void {
    if (!this.institutionId) {
      this.handleError('Institution ID is required', 'Failed to load schools.');
      return;
    }

    this.loadingSpinnerService.showSpinner();

    this.schoolsService
      .getInstitution(this.institutionId)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: (response) => {
          this.loadingSpinnerService.hideSpinner();
          
          if (response.success && response.entity) {
            const institution = response.entity;
            const schools = institution.institutionSchools || [];
            const mappedSchools = schools.map((school) => this.mapSchoolToTableRow(school));
            this.schoolsTableData.set(mappedSchools);
          } else {
            this.handleError(
              response.message || 'Failed to load institution schools.',
              'Failed to load schools. Please try again later.'
            );
            this.schoolsTableData.set([]);
          }
        },
        error: (error) => {
          this.loadingSpinnerService.hideSpinner();
          const errorMessage = error?.error?.message || 'Failed to load institution schools.';
          this.handleError(errorMessage, 'Failed to load schools. Please try again later.');
          this.schoolsTableData.set([]);
        }
      });
  }

  private mapSchoolToTableRow(school: School): SchoolTableRow {
    return {
      ...school,
      fullAddress: this.formatAddress(school),
      hdmaName: this.formatHdmaName(school),
      emails: this.formatEmail(school),
      phoneNumber: this.formatPhoneNumber(school),
      extension: this.formatExtension(school),
      schoolStatus: this.getSchoolStatus(school)
    };
  }

  private formatHdmaName(school: School): string {
    // Access nested hdmaContact.person structure from API response
    const schoolData = school as SchoolWithHdma;
    const person = schoolData.hdmaContact?.person;
    
    if (!person) {
      return '-';
    }
    
    const firstName = person.firstName || '';
    const middleName = person.middleName || '';
    const lastName = person.lastName || '';
    
    // Format: FirstName MiddleName LastName
    return [firstName, middleName, lastName]
      .filter(name => name.trim())
      .join(' ') || '-';
  }

  private formatEmail(school: School): string {
    // Access nested hdmaContact.person.personEmails structure from API response
    const schoolData = school as SchoolWithHdma;
    const personEmails = schoolData.hdmaContact?.person?.personEmails;
    
    if (!personEmails || personEmails.length === 0) {
      return '-';
    }
    
    // Get primary email or first email
    const primaryEmail = personEmails.find((email: PersonEmail) => email.isPrimary);
    return primaryEmail?.emailAddress || personEmails[0]?.emailAddress || '-';
  }

  private formatPhoneNumber(school: School): string {
    // Access nested hdmaContact.person.personPhones structure from API response
    const schoolData = school as SchoolWithHdma;
    const personPhones = schoolData.hdmaContact?.person?.personPhones;
    
    if (!personPhones || personPhones.length === 0) {
      return '-';
    }
    
    // Get primary phone or first phone
    const primaryPhone = personPhones.find((p: PersonPhone) => p.isPrimary);
    const phoneData = primaryPhone?.phone || personPhones[0]?.phone;
    
    if (!phoneData?.phoneNumber) {
      return '-';
    }
    
    return phoneData.phoneNumber;
  }

  private formatExtension(school: School): string {
    // Access nested hdmaContact.person.personPhones structure from API response
    const schoolData = school as SchoolWithHdma;
    const personPhones = schoolData.hdmaContact?.person?.personPhones;
    
    if (!personPhones || personPhones.length === 0) {
      return '-';
    }
    
    // Get primary phone or first phone
    const primaryPhone = personPhones.find((p: PersonPhone) => p.isPrimary);
    const phoneData = primaryPhone || personPhones[0];
    
    // Check both phoneData.extension and phoneData.phone.extension
    const extension = phoneData?.extension || phoneData?.phone?.extension;
    
    return extension ? String(extension) : '-';
  }

  private getSchoolStatus(school: School): string {
    // Access schoolStatus from API response
    const schoolData = school as SchoolWithHdma;
    return schoolData.schoolStatus || 'Approved';
  }

  private formatAddress(school: School): string {
    const parts: string[] = [];
    
    if (school.address1) {
      parts.push(school.address1);
    }
    if (school.address2) {
      parts.push(school.address2);
    }
    
    const cityStateZip: string[] = [];
    if (school.city) {
      cityStateZip.push(school.city);
    }
    if (school.state) {
      cityStateZip.push(school.state);
    }
    if (school.zip) {
      cityStateZip.push(school.zip);
    }
    
    if (cityStateZip.length > 0) {
      parts.push(cityStateZip.join(', '));
    }
    
    return parts.length > 0 ? parts.join(', ') : '-';
  }

  private handleError(errorMessage: string, toastMessage?: string): void {
    this.appInsightsService.log(new Error(errorMessage), SeverityLevel.error);
    if (toastMessage) {
      this.toastService.showToast(ToastMessageType.Error, toastMessage);
    } else {
      this.toastService.showToast(ToastMessageType.Error, errorMessage);
    }
  }

  handleHyperlinkClick(row: SchoolTableRow, column: string): void {
    if (column === 'name' && row.programPartnerId) {
      // Navigate to school record page with partnerTypeId query parameter
      // Route through institutions parent to maintain breadcrumb trail
      this.router.navigate(
        ['/institutions/institution', this.institutionId, 'edit', 'school', row.programPartnerId],
        { queryParams: { partnerTypeId: 1 } }
      );
    }
  }
}
